import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
    Inject,
    Injectable,
} from '@angular/core';
import {
    NavigationExtras,
    Router,
} from '@angular/router';
import { AppStateFacade } from '@app-state';
import { IUser } from '@models';
import { Mapper } from '@services/generics/data.mapper';
import { MapperService } from '@services/mapper.service';
import { ModalService } from '@services/modal.service';
import { StorageService } from '@services/storage.service';

import * as queryString from 'query-string';
import {
    BehaviorSubject,
    Observable,
} from 'rxjs';
import {
    finalize,
    map,
    tap,
} from 'rxjs/operators';

const defaultTenant = 'Default';
@Injectable({
    providedIn: 'root',
})
export class AuthService extends Mapper<IUser> {
    public pendingLogout$ = new BehaviorSubject(false);

    public get lastTenantUsed() {
        return this._storage.get(this._tenantCacheKey, defaultTenant);
    }

    public set lastTenantUsed(value: string) {
        this._storage.set(this._tenantCacheKey, value || defaultTenant);
    }

    private get _tenantCacheKey() {
        return `${this._appState.authConfig.buildVersion}-last-login-tenant`;
    }

    constructor(
        private _appState: AppStateFacade,
        private _http: HttpClient,
        private _router: Router,
        private _storage: StorageService,
        private _modalService: ModalService,
        @Inject(DOCUMENT)
        private _document: Document,
    ) {
        super();
    }

    public registerTenant(payload: any): Observable<any> {
        return this._http.post('/account/registertenant', JSON.stringify(payload));
    }

    public externalLoginTenant(tenant: string): Observable<any> {
        return this._http.post('/account/ExternalLoginTenant', { tenancyName: tenant });
    }

    public logout(params?: NavigationExtras) {
        this.pendingLogout$.next(true);
        this._modalService.closeAllDialogs();

        const logoutUrl = this._appState.authConfig.value.cloud.logoutUrl;
        if (logoutUrl) {
            this._http.get('/account/logout').subscribe(() => {
                window.location.href = logoutUrl;
            });
            return;
        }

        this._router.navigate(['/account/login'], params);

        this._appState.user.logout().pipe(
            finalize(() => {
                this.pendingLogout$.next(false);
                this._storage.set('loggedInUser', 'null');
            }),
            // This is necessary to clear Authentication cookies in IE.
            // Otherwise the subsequent requests will have the windows identity set.
            tap(() => this._document.execCommand('ClearAuthenticationCache', false)),
        ).subscribe();
    }

    public forgotPassword(payload: any) {
        return this._http.post('/account/forgotpassword', JSON.stringify(payload));
    }

    public changePassword(payload: any) {
        return this._http.post('/account/resetpassword', JSON.stringify(payload));
    }

    public changeExpiredPassword(data: any): Observable<any> {
        const payload = JSON.stringify(this._toPoco(data, MapperService.changeExpiredPasswordMap));
        return this._http.post('/account/changepassword', payload);
    }

    public getExternalLoginInfo(): Observable<any> {
        return this._http.get('/account/ExternalLoginInfo')
            .pipe(
                map((response: any) => {
                    if (response && response.result) {
                        return {
                            tenancyName: response.result.tenancyName,
                            emailAddress: response.result.emailAddress,
                            surname: response.result.surname,
                            name: response.result.name,
                        };
                    }
                }),
            );
    }

    public getExternalUserTenants(): Observable<any> {
        return this._http.get('/account/ExternalUserTenants')
            .pipe(
                map((response: any) => {
                    if (response) {
                        return response.result;
                    }
                }),
            );
    }

    public extractReturnUrl(url: string): { returnUrl: string, navigationParams: NavigationExtras } {
        let returnUrl = '/';
        const navigationParams: NavigationExtras = {};

        if (url) {
            const parsedRequestedRoute = queryString.parseUrl(url);
            returnUrl = parsedRequestedRoute.url;
            if (Object.keys(parsedRequestedRoute.query).length > 0) {
                navigationParams.queryParams = parsedRequestedRoute.query;
            }
        }

        return {
            returnUrl,
            navigationParams,
        };
    }
}
